import { Check, ImagePlus, Upload, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { uploadPhotos } from "../api";

function splitTags(value) {
  return value.split(/[，,]/).map((tag) => tag.trim()).filter(Boolean);
}

export function UploadSheet({ open, onClose, onComplete }) {
  const [files, setFiles] = useState([]);
  const [location, setLocation] = useState("");
  const [styles, setStyles] = useState("");
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const previews = useMemo(
    () => files.slice(0, 5).map((file) => ({ file, url: URL.createObjectURL(file) })),
    [files],
  );

  useEffect(() => () => previews.forEach((item) => URL.revokeObjectURL(item.url)), [previews]);
  useEffect(() => {
    if (!open) {
      setFiles([]);
      setLocation("");
      setStyles("");
      setProgress(0);
      setStatus("idle");
      setError("");
    }
  }, [open]);

  if (!open) return null;

  async function beginUpload() {
    if (!files.length || status === "uploading") return;
    setError("");
    setStatus("uploading");
    try {
      const result = await uploadPhotos({
        files,
        location,
        styles: splitTags(styles),
        onProgress: setProgress,
      });
      setProgress(100);
      setStatus("complete");
      window.setTimeout(() => onComplete(result.photos), 550);
    } catch (uploadError) {
      setError(uploadError.message);
      setStatus("error");
    }
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="upload-sheet" role="dialog" aria-modal="true" aria-labelledby="upload-title">
        <div className="sheet-handle" aria-hidden="true" />
        <header>
          <div>
            <h2 id="upload-title">导入到相册</h2>
            <p>原图留存，预览图自动生成</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭导入">
            <X size={24} strokeWidth={1.3} />
          </button>
        </header>

        <label className={`file-picker ${files.length ? "has-files" : ""}`}>
          <input
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            onChange={(event) => setFiles(Array.from(event.target.files || []))}
          />
          {files.length ? (
            <>
              <div className="preview-strip">
                {previews.map(({ file, url }) => <img src={url} alt="" key={`${file.name}-${file.lastModified}`} />)}
                {files.length > previews.length && <span>+{files.length - previews.length}</span>}
              </div>
              <strong>{files.length} 张照片已选择</strong>
              <small>轻触重新选择</small>
            </>
          ) : (
            <>
              <ImagePlus size={34} strokeWidth={1.15} aria-hidden="true" />
              <strong>选择照片</strong>
              <small>从 Android 相册或文件中选择，可多选</small>
            </>
          )}
        </label>

        <div className="upload-fields">
          <label className="field">
            <span>地点 <small>可稍后修改</small></span>
            <input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="例如：上海 · 徐汇" />
          </label>
          <label className="field">
            <span>风格 <small>用逗号分隔；留空将自动推断</small></span>
            <input value={styles} onChange={(event) => setStyles(event.target.value)} placeholder="柔光，胶片，夜色" />
          </label>
          <div className="storage-row">
            <span>保存至</span>
            <strong>这台电脑 / 私有云卷</strong>
          </div>
          <div className="storage-row">
            <span>保留原图</span>
            <strong className="storage-enabled"><Check size={15} /> 已开启</strong>
          </div>
        </div>

        {status === "uploading" || status === "complete" ? (
          <div className="upload-progress" aria-live="polite">
            <div><span>{status === "complete" ? "导入完成" : "正在上传"}</span><strong>{progress}%</strong></div>
            <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>
          </div>
        ) : null}
        {error && <p className="error-message" role="alert">{error}</p>}

        <button className="upload-action" type="button" onClick={beginUpload} disabled={!files.length || status === "uploading" || status === "complete"}>
          <Upload size={18} strokeWidth={1.5} />
          {status === "complete" ? "已导入" : status === "uploading" ? "正在导入…" : "开始导入"}
        </button>
      </section>
    </div>
  );
}
